import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Holder from "lib/icons/Holder";
import Button from "../../ui/button/Button";

type QuestionRowProps = {
  id: string;
  questionText: string;
  onStartEdit: () => void;
  onDelete: () => void;
};

export default function QuestionRow({
  id,
  questionText,
  onStartEdit,
  onDelete,
}: QuestionRowProps): JSX.Element {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <Row
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Flex gap={8} alignItems="center">
        <DragHandle
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <Holder />
        </DragHandle>

        <Text bold> {questionText}</Text>
      </Flex>
      <ButtonWrapper>
        <Button
          key="edit"
          icon="edit05"
          onClick={onStartEdit}
          aria-label="Edit"
          buttonType="tertiary"
          iconProps={{
            color: "sysTextDefault",
          }}
        />

        <Button
          key="delete"
          icon="trash03"
          onClick={onDelete}
          aria-label="Delete"
          buttonType="tertiary"
          iconProps={{
            color: "sysTextDefault",
          }}
        />
      </ButtonWrapper>
    </Row>
  );
}

const Row = styled(Flex)(({ theme }) => ({
  padding: "16px 8px",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: 8,
  gap: 8,
  "&:hover": {
    backgroundColor: theme.colors.sysBackgroundAlternative,
  },
}));

const DragHandle = styled(Flex)({
  opacity: 0,
  pointerEvents: "none",
  cursor: "grab",
  alignItems: "center",
  lineHeight: 0,
  flexShrink: 0,

  [`${Row}:hover &, &:focus-visible`]: {
    opacity: 1,
    pointerEvents: "auto",
  },

  "&:active": {
    cursor: "grabbing",
  },
});

const ButtonWrapper = styled(Flex)({
  opacity: 0,
  gap: 8,
  visibility: "hidden",
  transition: "opacity 0.2s, visibility 0.2s",

  [`${Row}:hover &, &:focus-within`]: {
    opacity: 1,
    visibility: "visible",
  },
});
